'use client';

/**
 * Engineering Ladder Builder
 * Design career frameworks with IC and management tracks
 */

import { useState, useEffect, useCallback } from 'react';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import {
  LadderFramework,
  LadderTemplate,
  Level,
  TrackType,
  LadderFeatures,
} from '@/lib/engineering-ladder/types';
import {
  getAllFrameworks,
  saveFramework,
  deleteFramework,
  createFromTemplate,
  getFramework,
} from '@/lib/engineering-ladder/storage';
import { LADDER_TEMPLATES, getStandardCompetencies } from '@/lib/engineering-ladder/templates';
import { useAuth } from '@/lib/auth/AuthContext';

// Dynamic imports for components that use client-side only features
const TrackSelector = dynamic(
  () => import('@/components/engineering-ladder/TrackSelector').then(m => ({ default: m.TrackSelector })),
  { ssr: false }
);

const LevelCard = dynamic(
  () => import('@/components/engineering-ladder/LevelCard').then(m => ({ default: m.LevelCard })),
  { ssr: false }
);

const CompetencyMatrix = dynamic(
  () => import('@/components/engineering-ladder/CompetencyMatrix').then(m => ({ default: m.CompetencyMatrix })),
  { ssr: false }
);

const LevelEditor = dynamic(
  () => import('@/components/engineering-ladder/LevelEditor').then(m => ({ default: m.LevelEditor })),
  { ssr: false }
);

const ExportOptions = dynamic(
  () => import('@/components/engineering-ladder/ExportOptions').then(m => ({ default: m.ExportOptions })),
  { ssr: false }
);

const ImportOptions = dynamic(
  () => import('@/components/engineering-ladder/ExportOptions').then(m => ({ default: m.ImportOptions })),
  { ssr: false }
);

const TemplateSelector = dynamic(
  () => import('@/components/engineering-ladder/TemplateSelector').then(m => ({ default: m.TemplateSelector })),
  { ssr: false }
);

// Premium tier hierarchy
const TIER_HIERARCHY = { free: 0, pro: 1, team: 2 };

// Get user's subscription tier (mock for now)
function useUserTier(): 'free' | 'pro' | 'team' {
  const { isAuthenticated } = useAuth();
  // In production, this would check the user's subscription
  return isAuthenticated ? 'pro' : 'free';
}

// Get feature access based on tier
function useFeatures(tier: 'free' | 'pro' | 'team'): LadderFeatures {
  const tierLevel = TIER_HIERARCHY[tier];
  return {
    canEdit: tierLevel >= TIER_HIERARCHY.pro,
    canSave: tierLevel >= TIER_HIERARCHY.pro,
    canExportMarkdown: tierLevel >= TIER_HIERARCHY.pro,
    canExportExcel: tierLevel >= TIER_HIERARCHY.team,
    canCustomizeCompetencies: tierLevel >= TIER_HIERARCHY.pro,
    maxFrameworks: tier === 'free' ? 1 : tier === 'pro' ? 10 : 999,
  };
}

type ViewMode = 'cards' | 'matrix' | 'timeline';
type ModalState = 'none' | 'template' | 'edit-level' | 'export' | 'import' | 'frameworks';

export default function EngineeringLadderPage() {
  const { isAuthenticated, login } = useAuth();
  const userTier = useUserTier();
  const features = useFeatures(userTier);

  // State
  const [isLoading, setIsLoading] = useState(true);
  const [framework, setFramework] = useState<LadderFramework | null>(null);
  const [savedFrameworks, setSavedFrameworks] = useState<LadderFramework[]>([]);
  const [selectedTrack, setSelectedTrack] = useState<TrackType>('ic');
  const [selectedLevelId, setSelectedLevelId] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('cards');
  const [modalState, setModalState] = useState<ModalState>('none');
  const [editingLevel, setEditingLevel] = useState<Level | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Load saved frameworks on mount
  useEffect(() => {
    async function loadData() {
      try {
        const frameworks = await getAllFrameworks();
        setSavedFrameworks(frameworks);

        // Load the most recent framework if available
        if (frameworks.length > 0) {
          setFramework(frameworks[0]);
        }
      } catch (error) {
        console.error('Failed to load frameworks:', error);
      } finally {
        setIsLoading(false);
      }
    }

    loadData();
  }, []);

  // Get current track levels
  const currentTrackLevels = framework?.tracks[selectedTrack].levels || [];
  const sortedLevels = [...currentTrackLevels].sort((a, b) => a.order - b.order);

  // Handle template selection
  const handleTemplateSelect = async (template: LadderTemplate) => {
    try {
      const newFramework = await createFromTemplate(
        template.framework,
        template.id === 'blank' ? undefined : `${template.name} (Custom)`
      );
      setFramework(newFramework);
      setSavedFrameworks(prev => [newFramework, ...prev]);
      setModalState('none');
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error('Failed to create framework:', error);
    }
  };

  // Handle framework import
  const handleImport = async (importedFramework: LadderFramework) => {
    try {
      await saveFramework(importedFramework);
      setFramework(importedFramework);
      setSavedFrameworks(prev => [importedFramework, ...prev.filter(f => f.id !== importedFramework.id)]);
      setModalState('none');
    } catch (error) {
      console.error('Failed to import framework:', error);
    }
  };

  // Save framework changes
  const handleSave = useCallback(async () => {
    if (!framework || !features.canSave) return;

    try {
      await saveFramework(framework);
      setSavedFrameworks(prev =>
        prev.map(f => f.id === framework.id ? framework : f)
      );
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error('Failed to save framework:', error);
    }
  }, [framework, features.canSave]);

  // Update framework
  const updateFramework = useCallback((updates: Partial<LadderFramework>) => {
    if (!framework) return;
    setFramework({ ...framework, ...updates });
    setHasUnsavedChanges(true);
  }, [framework]);

  // Add or update a level
  const handleLevelSave = useCallback((level: Level) => {
    if (!framework) return;

    const track = framework.tracks[level.track];
    const existingIndex = track.levels.findIndex(l => l.id === level.id);

    let newLevels: Level[];
    if (existingIndex >= 0) {
      newLevels = track.levels.map(l => l.id === level.id ? level : l);
    } else {
      // Assign order based on position
      const maxOrder = Math.max(0, ...track.levels.map(l => l.order));
      newLevels = [...track.levels, { ...level, order: maxOrder + 1 }];
    }

    updateFramework({
      tracks: {
        ...framework.tracks,
        [level.track]: {
          ...track,
          levels: newLevels,
        },
      },
    });

    setModalState('none');
    setEditingLevel(null);
  }, [framework, updateFramework]);

  // Delete a level
  const handleLevelDelete = useCallback((levelId: string) => {
    if (!framework || !confirm('Are you sure you want to delete this level?')) return;

    const track = framework.tracks[selectedTrack];
    updateFramework({
      tracks: {
        ...framework.tracks,
        [selectedTrack]: {
          ...track,
          levels: track.levels.filter(l => l.id !== levelId),
        },
      },
    });
  }, [framework, selectedTrack, updateFramework]);

  // Handle level edit
  const handleLevelEdit = useCallback((level: Level) => {
    setEditingLevel(level);
    setModalState('edit-level');
  }, []);

  // Switch to a different saved framework
  const handleFrameworkSelect = useCallback(async (frameworkId: string) => {
    try {
      const selected = await getFramework(frameworkId);
      if (selected) {
        setFramework(selected);
        setHasUnsavedChanges(false);
      }
      setModalState('none');
    } catch (error) {
      console.error('Failed to load framework:', error);
    }
  }, []);

  // Delete a framework
  const handleFrameworkDelete = useCallback(async (frameworkId: string) => {
    if (!confirm('Are you sure you want to delete this framework?')) return;

    try {
      await deleteFramework(frameworkId);
      setSavedFrameworks(prev => prev.filter(f => f.id !== frameworkId));
      if (framework?.id === frameworkId) {
        setFramework(savedFrameworks.find(f => f.id !== frameworkId) || null);
      }
    } catch (error) {
      console.error('Failed to delete framework:', error);
    }
  }, [framework, savedFrameworks]);

  // Render loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-foreground-secondary">Loading...</div>
      </div>
    );
  }

  // Render template selector if no framework
  if (!framework) {
    return (
      <div className="min-h-screen bg-background p-6">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <Link href="/tools" className="text-foreground-tertiary hover:text-foreground text-sm mb-4 inline-flex items-center gap-1">
              ← Back to Tools
            </Link>
            <h1 className="text-3xl font-bold text-foreground mt-2">Engineering Ladder Builder</h1>
            <p className="text-foreground-secondary mt-2">
              Design career frameworks with IC and management tracks, competency matrices, and progression criteria.
            </p>
          </div>

          {/* Premium badge */}
          <div className="mb-6 p-4 bg-purple-500/10 border border-purple-500/30 rounded-lg flex items-start gap-3">
            <span className="text-2xl">⭐</span>
            <div>
              <div className="font-semibold text-purple-400">Premium Feature</div>
              <p className="text-sm text-foreground-secondary">
                Full customization, saving, and export require a Pro subscription.
                {!isAuthenticated && ' Sign in to get started.'}
              </p>
            </div>
          </div>

          {/* Template selection */}
          <TemplateSelector
            onSelect={handleTemplateSelect}
            onClose={() => {}}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b border-border bg-background/95 backdrop-blur sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <Link href="/tools" className="text-foreground-tertiary hover:text-foreground">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
              </Link>
              <div>
                <h1 className="text-xl font-bold text-foreground">{framework.name}</h1>
                <p className="text-sm text-foreground-tertiary">Engineering Ladder Builder</p>
              </div>
              {hasUnsavedChanges && (
                <span className="text-xs px-2 py-1 bg-accent-muted text-accent rounded">
                  Unsaved changes
                </span>
              )}
            </div>

            <div className="flex items-center gap-2">
              {/* View mode selector */}
              <div className="flex gap-1 p-1 bg-cream/5 rounded">
                {(['cards', 'matrix', 'timeline'] as ViewMode[]).map((mode) => (
                  <button
                    key={mode}
                    onClick={() => setViewMode(mode)}
                    className={`px-3 py-1.5 text-sm rounded transition-colors ${
                      viewMode === mode
                        ? 'bg-accent text-accent-foreground'
                        : 'text-foreground-secondary hover:text-foreground'
                    }`}
                  >
                    {mode.charAt(0).toUpperCase() + mode.slice(1)}
                  </button>
                ))}
              </div>

              {/* Actions */}
              <button
                onClick={() => setModalState('frameworks')}
                className="p-2 text-foreground-tertiary hover:text-foreground"
                title="Saved frameworks"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                </svg>
              </button>

              <button
                onClick={() => setModalState('import')}
                className="p-2 text-foreground-tertiary hover:text-foreground"
                title="Import"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                </svg>
              </button>

              <button
                onClick={() => setModalState('export')}
                className="p-2 text-foreground-tertiary hover:text-foreground"
                title="Export"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
              </button>

              {features.canSave && (
                <button
                  onClick={handleSave}
                  disabled={!hasUnsavedChanges}
                  className={`px-4 py-2 rounded font-semibold transition-colors ${
                    hasUnsavedChanges
                      ? 'bg-accent text-accent-foreground hover:bg-accent-hover'
                      : 'bg-cream/10 text-foreground-tertiary cursor-not-allowed'
                  }`}
                >
                  Save
                </button>
              )}

              {!features.canSave && (
                <button
                  onClick={() => login()}
                  className="px-4 py-2 bg-purple-500 text-white rounded font-semibold hover:bg-purple-600"
                >
                  Upgrade to Save
                </button>
              )}
            </div>
          </div>

          {/* Track selector */}
          <div className="flex items-center justify-between">
            <TrackSelector
              selectedTrack={selectedTrack}
              onTrackChange={setSelectedTrack}
              icLevelCount={framework.tracks.ic.levels.length}
              managementLevelCount={framework.tracks.management.levels.length}
            />

            {features.canEdit && (
              <button
                onClick={() => {
                  setEditingLevel(null);
                  setModalState('edit-level');
                }}
                className="flex items-center gap-2 px-3 py-2 text-sm bg-cream/10 text-foreground rounded hover:bg-cream/20"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Level
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        {viewMode === 'cards' && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {sortedLevels.map((level) => (
              <LevelCard
                key={level.id}
                level={level}
                competencies={framework.competencies}
                isSelected={selectedLevelId === level.id}
                onClick={() => setSelectedLevelId(level.id === selectedLevelId ? null : level.id)}
                onEdit={features.canEdit ? () => handleLevelEdit(level) : undefined}
                onDelete={features.canEdit ? () => handleLevelDelete(level.id) : undefined}
                currency={framework.currency}
                canEdit={features.canEdit}
              />
            ))}
            {sortedLevels.length === 0 && (
              <div className="col-span-full text-center py-12 text-foreground-tertiary">
                No levels defined yet.
                {features.canEdit && ' Click "Add Level" to get started.'}
              </div>
            )}
          </div>
        )}

        {viewMode === 'matrix' && (
          <CompetencyMatrix
            levels={sortedLevels}
            competencies={framework.competencies}
            selectedLevelId={selectedLevelId || undefined}
            onLevelSelect={(id) => setSelectedLevelId(id === selectedLevelId ? null : id)}
          />
        )}

        {viewMode === 'timeline' && (
          <div className="flex gap-2 overflow-x-auto pb-4">
            {sortedLevels.map((level, index) => (
              <div key={level.id} className="flex items-center">
                <div
                  className={`
                    px-4 py-3 rounded-lg cursor-pointer transition-all min-w-[150px]
                    ${selectedLevelId === level.id
                      ? 'bg-accent text-accent-foreground'
                      : 'bg-cream/10 text-foreground hover:bg-cream/20'
                    }
                  `}
                  onClick={() => setSelectedLevelId(level.id === selectedLevelId ? null : level.id)}
                >
                  <div className="font-mono font-bold text-sm">{level.code}</div>
                  <div className="text-sm truncate">{level.title}</div>
                </div>
                {index < sortedLevels.length - 1 && (
                  <svg className="w-6 h-6 text-foreground-muted mx-1 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Upgrade prompt for free users */}
        {!features.canEdit && (
          <div className="mt-8 p-6 bg-purple-500/10 border border-purple-500/30 rounded-lg text-center">
            <h3 className="text-lg font-semibold text-purple-400 mb-2">
              Upgrade to Customize
            </h3>
            <p className="text-foreground-secondary mb-4 max-w-md mx-auto">
              With a Pro subscription, you can customize levels, add competencies,
              save multiple frameworks, and export to Markdown.
            </p>
            <button
              onClick={() => login()}
              className="px-6 py-2 bg-purple-500 text-white rounded font-semibold hover:bg-purple-600"
            >
              {isAuthenticated ? 'Upgrade to Pro' : 'Sign In to Upgrade'}
            </button>
          </div>
        )}
      </div>

      {/* Modals */}
      {modalState !== 'none' && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          {modalState === 'template' && (
            <TemplateSelector
              onSelect={handleTemplateSelect}
              onClose={() => setModalState('none')}
            />
          )}

          {modalState === 'edit-level' && (
            <LevelEditor
              level={editingLevel || undefined}
              track={selectedTrack}
              competencies={framework.competencies.length > 0 ? framework.competencies : getStandardCompetencies()}
              existingLevelCodes={currentTrackLevels.map(l => l.code)}
              onSave={handleLevelSave}
              onCancel={() => {
                setModalState('none');
                setEditingLevel(null);
              }}
              currency={framework.currency}
            />
          )}

          {modalState === 'export' && (
            <ExportOptions
              framework={framework}
              canExportMarkdown={features.canExportMarkdown}
              canExportExcel={features.canExportExcel}
              onClose={() => setModalState('none')}
            />
          )}

          {modalState === 'import' && (
            <ImportOptions
              onImport={handleImport}
              onClose={() => setModalState('none')}
            />
          )}

          {modalState === 'frameworks' && (
            <div className="bg-background border border-border rounded-lg p-6 max-w-lg w-full">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-foreground">Saved Frameworks</h2>
                <button
                  onClick={() => setModalState('none')}
                  className="p-1 text-foreground-tertiary hover:text-foreground"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {savedFrameworks.map((fw) => (
                  <div
                    key={fw.id}
                    className={`
                      flex items-center justify-between p-3 rounded border transition-colors
                      ${fw.id === framework.id
                        ? 'border-accent bg-accent-muted'
                        : 'border-border hover:border-border/40'
                      }
                    `}
                  >
                    <button
                      onClick={() => handleFrameworkSelect(fw.id)}
                      className="flex-1 text-left"
                    >
                      <div className="font-semibold text-foreground">{fw.name}</div>
                      <div className="text-xs text-foreground-tertiary">
                        {fw.tracks.ic.levels.length} IC • {fw.tracks.management.levels.length} Management
                      </div>
                    </button>
                    {fw.id !== framework.id && (
                      <button
                        onClick={() => handleFrameworkDelete(fw.id)}
                        className="p-1 text-foreground-tertiary hover:text-red-400"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    )}
                  </div>
                ))}
                {savedFrameworks.length === 0 && (
                  <p className="text-foreground-tertiary text-center py-4">No saved frameworks</p>
                )}
              </div>

              <div className="mt-4 pt-4 border-t border-border flex gap-2">
                <button
                  onClick={() => setModalState('template')}
                  className="flex-1 px-4 py-2 bg-cream/10 text-foreground rounded hover:bg-cream/20"
                >
                  New from Template
                </button>
                <button
                  onClick={() => setModalState('import')}
                  className="flex-1 px-4 py-2 bg-cream/10 text-foreground rounded hover:bg-cream/20"
                >
                  Import
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
